import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { ClinicalCommandVisual } from "@/components/ClinicalCommandVisual";

const lines = ["Everything your", "practice runs on -", "now intelligent."];
const ease = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen flex items-center overflow-hidden bg-white cyan-glow pt-20 pb-10">
      <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />

      <div className="relative z-20 max-w-[1600px] w-full mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#D8E6F1] bg-white/70 backdrop-blur px-4 py-1.5 mb-6"
          >
            <Sparkles size={14} className="text-[#2880B8]" />
            <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#51667A]">The Complete Practice OS</span>
          </motion.div>

          <h1 className="font-display tracking-tighter leading-[1.02] text-[#0C2A40]">
            {lines.map((line, i) => (
              <span key={i} className="line-mask pb-2">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.3 + i * 0.14, duration: 0.9, ease }}
                  className={`block text-4xl md:text-6xl lg:text-[5.2vw] ${i === 2 ? "font-extrabold" : "font-medium"}`}
                >
                  {i === 2 ? (
                    <>
                      now <span className="text-[#2880B8]">intelligent.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-6 max-w-xl text-[#51667A] text-lg leading-relaxed"
          >
            EHR + AI Scribe, PMS, RCM, HRMS - unified in one intelligent platform, with secure chat, calls and telehealth built in. One login. Zero silos.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#book"
              data-testid="hero-cta-demo"
              className="group font-mono text-[12px] tracking-[0.15em] uppercase px-7 py-4 rounded-full bg-[#2880B8] text-white hover:bg-[#1F6EA1] transition-colors duration-300 flex items-center gap-2 shadow-[0_16px_40px_-12px_rgba(40,128,184,0.6)]"
            >
              Get a Demo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#pricing"
              data-testid="hero-cta-pricing"
              className="font-mono text-[12px] tracking-[0.15em] uppercase px-7 py-4 rounded-full border border-[#D8E6F1] text-[#0C2A40] hover:border-[#2880B8] hover:text-[#2880B8] transition-colors duration-300"
            >
              See Pricing
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.45, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-6 text-[#51667A]"
          >
            <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] uppercase"><ShieldCheck size={15} className="text-[#2880B8]" /> 100% HIPAA</span>
            <span className="font-mono text-[11px] tracking-[0.12em] uppercase">Live in days, not months</span>
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1, ease }}
            className="relative rounded-2xl overflow-hidden border border-[#D8E6F1] shadow-[0_40px_80px_-30px_rgba(10,42,49,0.25)]"
          >
            <ClinicalCommandVisual />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
